import { generateRandomHex, request } from '@nextgen-web-framework/core';
import {
  FilterOption,
  PaymentOption,
  PaymentDetails,
  PaymentMethodProvider,
  CardInfo,
  CreatePaymentRequest,
  CreateTransactionRequest,
  Payment,
  ConfirmPaymentRequest,
  Amount,
} from '../models';
import { Projection } from '../models/Projection';
import { PmpProjection } from '../models/PmpProjection';
import type { CustomRequestOptions } from '../../types';
import { StoreParam } from '../models/StoreParam';

const appServiceUrl = '/compaymentas/v1';

const basePath = (baseURL: string) => baseURL + appServiceUrl;

export const PaymentApi = {
  get basePath() {
    const baseUrl = ApiConfigService.getBaseUrl();
    return `${baseUrl}${appServiceUrl}`;
  },

  async getPaymentOptions(filterOption: FilterOption, projectionType: PmpProjection, options?: CustomRequestOptions) {
    return await request.post<PaymentOption[]>({
      url: `${
        options?.baseURL ? basePath(options.baseURL) : this.basePath
      }/applicable-payment-method-providers?projection=${projectionType}`,
      body: filterOption as unknown as BodyInit,
      ...(options?.headers && { headers: options?.headers }),
    });
  },

  async getPaymentById(paymentId: string, options?: CustomRequestOptions): Promise<PaymentDetails> {
    return await request.get({
      url: `${options?.baseURL ? basePath(options.baseURL) : this.basePath}/payments/${paymentId}?projection=${
        Projection.Detailed
      }`,
      ...(options?.headers && { headers: options?.headers }),
    });
  },

  async getPayments(
    refIdParam: string | string[],
    refType?: string,
    errorCallback?: Function,
    options?: CustomRequestOptions,
  ): Promise<PaymentDetails[]> {
    const refId = typeof refIdParam === 'string' ? [refIdParam] : refIdParam;
    return await request.post({
      url: `${
        options?.baseURL ? basePath(options.baseURL) : this.basePath
      }/payment-details?refType=${refType}&projection=${Projection.Detailed}`,
      body: {
        refId: refId,
      } as unknown as BodyInit,
      errorHandlers: {
        '404': (e) => {
          errorCallback?.(e);
        },
      },
      ...(options?.headers && { headers: options?.headers }),
    });
  },

  async getAllPaymentMethods(options?: CustomRequestOptions): Promise<PaymentMethodProvider[]> {
    return await request.get({
      url: `${options?.baseURL ? basePath(options.baseURL) : this.basePath}/payment-method-providers`,
      ...(options?.headers && { headers: options?.headers }),
    });
  },

  async cancelPayment(
    paymentId: string,
    transactionId: string,
    options?: CustomRequestOptions,
    skipIdempotency = false,
  ) {
    return await request.post<PaymentDetails>(
      {
        url: `${
          options?.baseURL ? basePath(options.baseURL) : this.basePath
        }/payments/${paymentId}/transactions/${transactionId}/entries?projection=${Projection.Detailed}`,
        body: {
          entryType: 'CANCEL',
        } as unknown as BodyInit,
        ...(options?.headers && { headers: options?.headers }),
      },
      {
        skipIdempotency,
      },
    );
  },

  async getPaymentByCardId(cardId: string, options?: CustomRequestOptions): Promise<CardInfo> {
    return await request.get({
      url: `${options?.baseURL ? basePath(options.baseURL) : this.basePath}/card-info/${cardId}`,
      ...(options?.headers && { headers: options?.headers }),
    });
  },

  async getPaymentForRenewal(filterCategory?: string, options?: CustomRequestOptions): Promise<CardInfo[]> {
    return await request.get({
      url: `${options?.baseURL ? basePath(options.baseURL) : this.basePath}/card-info?filterCategory=${filterCategory}`,
      ...(options?.headers && { headers: options?.headers }),
    });
  },

  async createPayment(payload: CreatePaymentRequest, options?: CustomRequestOptions, skipIdempotency = false) {
    return await request.post<PaymentDetails>(
      {
        url: `${options?.baseURL ? basePath(options.baseURL) : this.basePath}/payments`,
        ...(options?.headers && { headers: options?.headers }),
        body: payload as unknown as BodyInit,
      },
      {
        skipIdempotency,
      },
    );
  },

  async addNewTransaction(
    payload: CreateTransactionRequest,
    paymentId: string,
    projectionType: Projection,
    options?: CustomRequestOptions,
    skipIdempotency = false,
  ) {
    const uuid = `${generateRandomHex(9)}-${generateRandomHex(4)}-${generateRandomHex(4)}-${generateRandomHex(
      4,
    )}-${generateRandomHex(13)}`;
    return await request.post<PaymentDetails>(
      {
        url: `${
          options?.baseURL ? basePath(options.baseURL) : this.basePath
        }/payments/${paymentId}/transactions?projection=${projectionType}`,
        ...(options?.headers && { headers: options?.headers }),
        body: payload as unknown as BodyInit,
        headers: {
          'X-Idempotency-Key': uuid,
          ...options?.headers,
        },
      },
      {
        skipIdempotency,
      },
    );
  },

  async confirmPayment(
    payload: ConfirmPaymentRequest,
    paymentId: string,
    options?: CustomRequestOptions,
    skipIdempotency = false,
  ) {
    return await request.patch<Payment>(
      {
        url: `${options?.baseURL ? basePath(options.baseURL) : this.basePath}/payments/${paymentId}/confirm-payment`,
        ...(options?.headers && { headers: options?.headers }),
        body: payload as unknown as BodyInit,
      },
      {
        skipIdempotency,
      },
    );
  },

  async voidPayment(
    paymentId: string,
    transactionId: string,
    projectionType: Projection,
    amount?: Amount,
    storeParams?: StoreParam,
    options?: CustomRequestOptions,
  ) {
    return await request.post<PaymentDetails>({
      url: `${
        options?.baseURL ? basePath(options.baseURL) : this.basePath
      }/payments/${paymentId}/transactions/${transactionId}/entries?projection=${projectionType}`,
      body: {
        entryType: 'VOID',
        amount,
        storeParams,
      } as unknown as BodyInit,
      ...(options?.headers && { headers: options?.headers }),
    });
  },

  async getRefundByTrxnId(
    paymentId: string,
    trxnId: string,
    projectionType: Projection,
    options?: CustomRequestOptions,
  ): Promise<CardInfo> {
    return await request.post({
      url: `${
        options?.baseURL ? basePath(options.baseURL) : this.basePath
      }/payments/${paymentId}/transactions/${trxnId}/entries?projection=${projectionType}`,
      ...(options?.headers && { headers: options?.headers }),
      body: {
        entryType: 'REFUND',
      } as unknown as BodyInit,
    });
  },
};
